import { useState } from "react";
import {
  PortablePipeline,
  remapStageIDsCSV,
} from "../../entities/pipeline/portablePipeline";
import {
  useCreatePipelineMutation,
  useLazyGetPipelineQuery,
  useUpdatePipelineMutation,
} from "../../redux/pipeline/pipelineEndpoints";
import {
  useCreateStageMutation,
  useDeleteStageMutation,
  useUpdateStageMutation,
} from "../../redux/stage/stageEndpoints";
import {
  useCreateInteractorMutation,
  useDeleteInteractorMutation,
} from "../../redux/interactor/interactorEndpoints";

export type ImportMode = "create" | "override";

export interface ImportProgress {
  /** Human readable description of the current step. */
  phase: string;
  current: number;
  total: number;
}

export interface ImportResult {
  pipelineID: number;
  stagesCreated: number;
  interactorsCreated: number;
  stagesDeleted: number;
  interactorsDeleted: number;
}

interface RunImportArgs {
  payload: PortablePipeline;
  mode: ImportMode;
  /** Required when mode === "override": the pipeline to replace. */
  targetPipelineID?: number;
}

/**
 * Materialize a portable pipeline into the current environment.
 *
 * The mercury backend has no import endpoint, so the pipeline graph is rebuilt
 * client side from the existing CRUD endpoints. Because stages and interactors
 * receive brand new IDs on creation, intra-pipeline references
 * (`available` / `excluded` / `segueID`) are remapped through an old -> new ID
 * map once every stage exists, mirroring the backend `duplicate` behavior.
 *
 * In `override` mode the target pipeline's own stages (and their interactors)
 * are deleted first so the result is an exact mirror of the imported pipeline —
 * this is the "push Live config onto Beta" use case. Inherited stages are never
 * touched.
 *
 * @returns The `runImport` runner plus reactive `running` / `progress` state.
 */
export function usePipelineImport() {
  const [getPipeline] = useLazyGetPipelineQuery();
  const [createPipeline] = useCreatePipelineMutation();
  const [updatePipeline] = useUpdatePipelineMutation();
  const [createStage] = useCreateStageMutation();
  const [updateStage] = useUpdateStageMutation();
  const [deleteStage] = useDeleteStageMutation();
  const [createInteractor] = useCreateInteractorMutation();
  const [deleteInteractor] = useDeleteInteractorMutation();

  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<ImportProgress | null>(null);

  const runImport = async ({
    payload,
    mode,
    targetPipelineID,
  }: RunImportArgs): Promise<ImportResult> => {
    setRunning(true);
    const body = payload.pipeline;
    let pipelineID: number;
    let stagesDeleted = 0;
    let interactorsDeleted = 0;

    try {
      if (mode === "override") {
        if (!targetPipelineID) {
          throw new Error("No target pipeline was selected to override.");
        }
        pipelineID = targetPipelineID;

        // Read the live target so we know exactly which stages/interactors to
        // clear. Only the pipeline's own entities are removed; inherited stages
        // (pipelineID !== target) are left untouched.
        setProgress({ phase: "Reading target pipeline", current: 0, total: 1 });
        const target = await getPipeline(targetPipelineID).unwrap();
        const ownStages = (target.stages ?? []).filter(
          (s) => s.pipelineID === targetPipelineID
        );
        const ownInteractors = ownStages.flatMap((s) => s.interactors ?? []);

        setProgress({
          phase: "Clearing existing interactors",
          current: 0,
          total: ownInteractors.length,
        });
        for (let i = 0; i < ownInteractors.length; i++) {
          const id = ownInteractors[i].id;
          if (id != null) {
            await deleteInteractor(id).unwrap();
            interactorsDeleted++;
          }
          setProgress({
            phase: "Clearing existing interactors",
            current: i + 1,
            total: ownInteractors.length,
          });
        }

        setProgress({
          phase: "Clearing existing stages",
          current: 0,
          total: ownStages.length,
        });
        for (let i = 0; i < ownStages.length; i++) {
          await deleteStage(ownStages[i].id).unwrap();
          stagesDeleted++;
          setProgress({
            phase: "Clearing existing stages",
            current: i + 1,
            total: ownStages.length,
          });
        }

        // Mirror the pipeline-level options. organizationID is intentionally
        // left as-is: it is environment specific and overriding it could move
        // the pipeline to a different org.
        await updatePipeline({
          id: pipelineID,
          body: {
            name: body.name,
            public: body.public,
            inherit: body.inherit,
            excludedStageIDs: body.excludedStageIDs,
            excludedInteractorIDs: body.excludedInteractorIDs,
          },
        }).unwrap();
      } else {
        setProgress({ phase: "Creating pipeline", current: 0, total: 1 });
        const created = await createPipeline({
          name: body.name,
          organizationID: body.organizationID,
          public: body.public,
          inherit: body.inherit,
          excludedStageIDs: body.excludedStageIDs,
          excludedInteractorIDs: body.excludedInteractorIDs,
        }).unwrap();
        if (created.id == null) {
          throw new Error("The pipeline could not be created.");
        }
        pipelineID = created.id;
      }

      const stages = body.stages ?? [];

      // Phase 1 — create every stage first (without routing) so we learn the
      // new ID of each one before wiring them together.
      const idMap: Record<number, number> = {};
      setProgress({
        phase: "Creating stages",
        current: 0,
        total: stages.length,
      });
      for (let i = 0; i < stages.length; i++) {
        const s = stages[i];
        const createdStage = await createStage({
          pipelineID,
          name: s.name,
          keyName: s.keyName,
          icon: s.icon,
          color: s.color,
          primary: s.primary,
          terminus: s.terminus,
          messaging: s.messaging,
          manual: s.manual,
          nudgeable: s.nudgeable,
          silent: s.silent,
          position: s.position ?? "0,0",
          config: s.config ?? "{}",
          acknowledgmentRequired: s.acknowledgmentRequired ?? false,
          available: "",
          excluded: "",
        }).unwrap();
        if (s.id != null && createdStage.id != null) {
          idMap[s.id] = createdStage.id;
        }
        setProgress({
          phase: "Creating stages",
          current: i + 1,
          total: stages.length,
        });
      }

      // Phase 2 — apply the remapped routing now that every stage exists.
      setProgress({
        phase: "Linking stages",
        current: 0,
        total: stages.length,
      });
      for (let i = 0; i < stages.length; i++) {
        const s = stages[i];
        const newID = s.id != null ? idMap[s.id] : undefined;
        if (newID != null) {
          const available = remapStageIDsCSV(s.available, idMap);
          const excluded = remapStageIDsCSV(s.excluded, idMap);
          if (available || excluded) {
            await updateStage({
              id: newID,
              body: {
                id: newID,
                pipelineID,
                name: s.name,
                keyName: s.keyName,
                icon: s.icon,
                color: s.color,
                primary: s.primary,
                terminus: s.terminus,
                messaging: s.messaging,
                manual: s.manual,
                nudgeable: s.nudgeable,
                silent: s.silent,
                position: s.position ?? "0,0",
                config: s.config ?? "{}",
                acknowledgmentRequired: s.acknowledgmentRequired ?? false,
                available,
                excluded,
              },
            }).unwrap();
          }
        }
        setProgress({
          phase: "Linking stages",
          current: i + 1,
          total: stages.length,
        });
      }

      // Phase 3 — recreate the interactors, pointing at the new stage IDs.
      const pendingInteractors = stages.flatMap((s) =>
        (s.interactors ?? []).map((interactor) => ({
          interactor,
          parentOldID: s.id,
        }))
      );
      let interactorsCreated = 0;
      setProgress({
        phase: "Creating interactors",
        current: 0,
        total: pendingInteractors.length,
      });
      for (let i = 0; i < pendingInteractors.length; i++) {
        const { interactor, parentOldID } = pendingInteractors[i];
        const stageID = parentOldID != null ? idMap[parentOldID] : undefined;
        if (stageID != null) {
          const segueID =
            interactor.segueID != null && idMap[interactor.segueID] != null
              ? idMap[interactor.segueID]
              : undefined;
          await createInteractor({
            stageID,
            name: interactor.name,
            keyName: interactor.keyName,
            icon: interactor.icon,
            color: interactor.color,
            mandatory: interactor.mandatory,
            title: interactor.title,
            description: interactor.description,
            kind: interactor.kind,
            segueID,
            data: interactor.data,
            order: interactor.order,
            acknowledgment: interactor.acknowledgment ?? false,
            config: interactor.config,
          }).unwrap();
          interactorsCreated++;
        }
        setProgress({
          phase: "Creating interactors",
          current: i + 1,
          total: pendingInteractors.length,
        });
      }

      setProgress(null);
      return {
        pipelineID,
        stagesCreated: Object.keys(idMap).length,
        interactorsCreated,
        stagesDeleted,
        interactorsDeleted,
      };
    } finally {
      setRunning(false);
    }
  };

  return { runImport, running, progress };
}
